import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Voice, { SpeechErrorEvent, SpeechResultsEvent } from '@react-native-voice/voice'
import { Platform, TouchableOpacity } from 'react-native'
import { SearchingPage } from '../SearchingPage/SearchingPage'
import { SearchInput } from './SearchInput'
import { MicIcon } from '../../Svg'

interface Props {
  placeholder: string
  getSearchValue: (value: string) => void
  hasInput?: boolean
}

const InitialInputVoiceSearchComponent = ({ placeholder, getSearchValue, hasInput }: Props) => {
  const { i18n } = useTranslation()

  const [started, setStarted] = useState(false)
  const [voiceError, setVoiceError] = useState(false)
  const [voiceMode, setVoiceMode] = useState(false)
  const [voiceResults, setVoiceResults] = useState<string[]>([])
  const [valueInput, setValueInput] = useState('')

  const handleVoiceMode = (value: boolean) => setVoiceMode(value)

  const handleStarted = (value: boolean) => setStarted(value)
  const handleVoiceError = (value: boolean) => setVoiceError(value)
  const handleVoiceResults = (value: string[]) => setVoiceResults(value)
  useEffect(() => {
    Voice.onSpeechError = onSpeechError
    Voice.onSpeechResults = onSpeechResults
    Voice.onSpeechEnd = () => handleStarted(false)

    return () => {
      Voice.destroy().then(Voice.removeAllListeners)
    }
  }, [])

  useEffect(() => {
    if (!voiceMode) Voice.destroy().then(Voice.removeAllListeners)
  }, [voiceMode])

  const onSpeechResults = async (result: SpeechResultsEvent) => {
    handleVoiceResults(result.value ?? [])
    if (Platform.OS === 'android') await Voice.cancel()
  }

  const onSpeechError = async (error: SpeechErrorEvent) => {
    handleStarted(false)
    handleVoiceError(true)
    console.error(error)
  }

  const startSpeechToText = async () => {
    try {
      if (voiceMode) {
        stopSpeechToText()
        handleVoiceMode(false)
      } else {
        startListening()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const stopSpeechToText = async () => {
    handleStarted(false)
    if (Platform.OS === 'android') {
      handleVoiceMode(false)
    }
    await Voice.cancel()
  }

  const startListening = async () => {
    handleVoiceMode(true)
    handleVoiceError(false)
    await Voice.start(i18n.language === 'en' ? 'en-US' : 'es-ES')
    handleStarted(true)
  }

  const selectNewWord = useCallback(
    async (word: string) => {
      getSearchValue(word)
      setValueInput(word)
      if (Platform.OS === 'ios') await Voice.cancel()
    },
    [getSearchValue],
  )

  return (
    <>
      <SearchingPage
        handleStarted={handleStarted}
        getValue={selectNewWord}
        handleVoiceMode={handleVoiceMode}
        started={started}
        startListening={startListening}
        stopListening={stopSpeechToText}
        voiceError={voiceError}
        voiceMode={voiceMode}
        voiceResults={voiceResults}
      />
      {!hasInput && (
        <TouchableOpacity onPress={() => startSpeechToText()}>
          <MicIcon width={20} height={20} />
        </TouchableOpacity>
      )}
      {hasInput && (
        <SearchInput
          startSpeechToText={startSpeechToText}
          placeholder={placeholder}
          getValue={selectNewWord}
          initValue={valueInput}
        />
      )}
    </>
  )
}

export const InitialInputVoiceSearch = React.memo(InitialInputVoiceSearchComponent)
